import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Account, BillingGroup, BillingGroupSaveRequest, GUID, Tenant } from '@ndvr/billing-api';

import { BillingApi } from 'API';
import { useSelectedTenantGUIDs } from 'views/auth/tenant-selection';
import { Toast } from 'widgets';

const BillingGroupQueryKeys = {
    all: ['billingGroups'] as const,
    byTenants: (tenantGUIDs: GUID<Tenant>[]) => ['billingGroups', 'tenants', tenantGUIDs],
    byAccounts: (accountGUIDs: GUID<Account>[]) => ['billingGroups', 'accounts', [...accountGUIDs].sort()],
} as const;

export const useBillingGroups = () => {
    const tenantGUIDs = useSelectedTenantGUIDs();

    return useQuery({
        queryKey: BillingGroupQueryKeys.byTenants(tenantGUIDs),
        enabled: tenantGUIDs.length > 0,
        queryFn: async (): Promise<BillingGroup[]> => {
            const [response] = await BillingApi().getBillingGroupsForTenants(tenantGUIDs);
            return response ?? [];
        },
    });
};

export const useBillingGroupsForAccounts = (accountGUIDs: GUID<Account>[]) => {
    return useQuery({
        queryKey: BillingGroupQueryKeys.byAccounts(accountGUIDs),
        enabled: accountGUIDs.length > 0,
        queryFn: async (): Promise<BillingGroup[]> => {
            const [response] = await BillingApi().getBillingGroupsContainingAnyAccount(accountGUIDs);
            return response ?? [];
        },
    });
};

export const useCreateBillingGroup = (tenantGUID: GUID<Tenant>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (request: BillingGroupSaveRequest) =>
            BillingApi({ hideError: true }).createBillingGroup(tenantGUID, request),
        onSuccess: () => {
            toast.success('Billing group created');
            queryClient.invalidateQueries({ queryKey: BillingGroupQueryKeys.all });
        },
        onError: (error) => Toast.forError(error, 'Failed to create billing group'),
    });
};

export const useUpdateBillingGroup = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            billingGroupGUID,
            request,
        }: {
            billingGroupGUID: GUID<BillingGroup>;
            request: BillingGroupSaveRequest;
        }) => BillingApi({ hideError: true }).updateBillingGroup(billingGroupGUID, request),
        onSuccess: () => {
            toast.success('Billing group updated');
            queryClient.invalidateQueries({ queryKey: BillingGroupQueryKeys.all });
        },
        onError: (error) => Toast.forError(error, 'Failed to update billing group'),
    });
};

export const useDeactivateBillingGroups = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (billingGroupGUIDs: GUID<BillingGroup>[]) =>
            BillingApi({ hideError: true }).deactivateBillingGroups(billingGroupGUIDs),
        onSuccess: (_data, billingGroupGUIDs) => {
            toast.success(`Billing group${billingGroupGUIDs.length === 1 ? '' : 's'} deactivated`);
            queryClient.invalidateQueries({ queryKey: BillingGroupQueryKeys.all });
        },
        onError: (error) => Toast.forError(error, 'Failed to deactivate billing groups'),
    });
};

export const useActivateBillingGroups = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (billingGroupGUIDs: GUID<BillingGroup>[]) =>
            BillingApi({ hideError: true }).activateBillingGroups(billingGroupGUIDs),
        onSuccess: (_data, billingGroupGUIDs) => {
            toast.success(`Billing group${billingGroupGUIDs.length === 1 ? '' : 's'} activated`);
            queryClient.invalidateQueries({ queryKey: BillingGroupQueryKeys.all });
        },
        onError: (error) => Toast.forError(error, 'Failed to activate billing groups'),
    });
};
